import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@mui/system';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import GlobalStyles from '@mui/material/GlobalStyles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ApiFetch } from '../App';
import { PerformancePlot } from './PerformancePlot';

// ─── Types (mirroring backend AssessmentReportSnapshot) ──────────────────────

type NormaColor = 'rosso' | 'giallo' | 'verde' | 'n/d';

interface NormeValori {
  Conoscersi: string;
  Costruire: string;
  Discutere: string;
  Proattività: string;
  Contesto: string;
  Connettersi: string;
}

interface AssessmentResult {
  probabilita_performance: NormaColor;
  performance_percepita: string;
  warnings: string[];
  norme: Record<string, string>;
  normeValori: NormeValori;
  voce_bedrock: string;
  ppt: {
    perceptionPerf: NormaColor;
    probabilityPerf: NormaColor;
    normsColors: NormaColor[];
    warnings: string[];
    voce_bedrock: string;
  };
}

interface AssessmentReportSnapshot {
  teamName: string;
  isRelational: boolean;
  analyzedAt: string;
  result: AssessmentResult;
}

// ─── Animations ──────────────────────────────────────────────────────────────

const cellBrighten = keyframes`
  from { filter: brightness(0.35); }
  to   { filter: brightness(1); }
`;

// ─── Constants ───────────────────────────────────────────────────────────────

const WARNING_KEY_MAP: Record<string, string> = {
  Frustrazione: 'analysis.warning_frustrazione',
  'Capacità di problem solving': 'analysis.warning_problem_solving',
  Stanchezza: 'analysis.warning_stanchezza',
  'Eccessiva zona di comfort': 'analysis.warning_comfort',
  'Orientamento tattico': 'analysis.warning_tattico',
  'Difficoltà nella messa a terra': 'analysis.warning_messa_a_terra',
  Frenesia: 'analysis.warning_frenesia',
  'Eccessiva tranquillità': 'analysis.warning_tranquillita',
  'Scarso apprendimento': 'analysis.warning_apprendimento',
  'Scarsa capacità realizzativa': 'analysis.warning_realizzativa',
  'Rischi di turnover': 'analysis.warning_turnover',
  'Scarsa spinta': 'analysis.warning_spinta',
};

const COLOR_HEX: Record<NormaColor, string> = {
  verde: '#2e7d32',
  giallo: '#e65100',
  rosso: '#c62828',
  'n/d': '#616161',
};

// normsColors order from backend: [Conoscersi, Discutere, Contesto, Costruire, Proattività, Connettersi]
const NORM_META = [
  { key: 'Conoscersi' as keyof NormeValori, labelKey: 'report.norm_conoscersi' },
  { key: 'Discutere' as keyof NormeValori, labelKey: 'report.norm_discutere' },
  { key: 'Contesto' as keyof NormeValori, labelKey: 'report.norm_contesto' },
  { key: 'Costruire' as keyof NormeValori, labelKey: 'report.norm_costruire' },
  { key: 'Proattività' as keyof NormeValori, labelKey: 'report.norm_proattivita' },
  { key: 'Connettersi' as keyof NormeValori, labelKey: 'report.norm_connettersi' },
];

const NORM_COLUMNS = [
  { headingKey: 'report.col_individuo', indices: [0, 3] },
  { headingKey: 'report.col_gruppo', indices: [1, 4] },
  { headingKey: 'report.col_organizzazione', indices: [2, 5] },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function NormsHeatmap({ normsColors }: { normsColors: NormaColor[] }) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = useState(false);
  const wasFullyVisible = useRef<boolean | null>(null);
  const hasLeftScreen = useRef(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const fullVisibilityObserver = new IntersectionObserver(
      ([entry]) => {
        const entering = entry.isIntersecting && wasFullyVisible.current === false;
        wasFullyVisible.current = entry.isIntersecting;
        if (entering && hasLeftScreen.current) {
          hasLeftScreen.current = false;
          setAnimating(false);
          requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
        }
      },
      { threshold: 1.0, rootMargin: '0px 0px -20px 0px' }
    );

    const exitObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          hasLeftScreen.current = true;
          setAnimating(false);
        }
      },
      { threshold: 0 }
    );

    fullVisibilityObserver.observe(el);
    exitObserver.observe(el);
    return () => {
      fullVisibilityObserver.disconnect();
      exitObserver.disconnect();
    };
  }, []);

  return (
    <Grid ref={containerRef} container spacing={2} sx={{ mb: 4 }}>
      {NORM_COLUMNS.map(({ headingKey, indices }, ci) => (
        <Grid key={headingKey} size={{ xs: 12, sm: 4 }}>
          <Typography
            variant="overline"
            fontWeight={700}
            color="text.secondary"
            align="center"
            display="block"
            sx={{ mb: 1 }}
          >
            {t(headingKey)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {indices.map((i) => {
              const { key, labelKey } = NORM_META[i];
              const color = normsColors[i] ?? 'n/d';
              return (
                <Box
                  key={key}
                  className="norm-cell"
                  sx={{
                    bgcolor: COLOR_HEX[color],
                    borderRadius: 1.5,
                    minHeight: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    textAlign: 'center',
                    p: 2,
                    filter: animating ? undefined : 'brightness(0.35)',
                    animation: animating
                      ? `${cellBrighten} 1.2s ease-out ${ci * 0.6}s both`
                      : 'none',
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    {t(labelKey)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AssessmentReportProps {
  surveyId: string;
  apiFetch: ApiFetch;
  mode: 'dark' | 'light';
  toggleMode: () => void;
}

export const AssessmentReport: React.FC<AssessmentReportProps> = ({
  surveyId,
  apiFetch,
  mode,
  toggleMode,
}) => {
  const { t, i18n } = useTranslation();
  const { palette } = useTheme();
  const primary = palette.primary.main;
  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  };
  const [snapshot, setSnapshot] = useState<AssessmentReportSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await apiFetch({ operation: 'get_report', surveyId, lang: i18n.language });
        if (!response.ok) {
          setError(t('report.load_error'));
          return;
        }
        const json = await response.json();
        if (json['output']?.error) {
          setError(json['output'].error);
        } else {
          setSnapshot(json['output'] as AssessmentReportSnapshot);
        }
      } catch {
        setError(t('report.network_error'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !snapshot) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error">{error ?? t('report.not_found')}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => (window.location.href = '/')}>
          {t('report.back_to_app')}
        </Button>
      </Box>
    );
  }

  const normaColorToLevel = (c: NormaColor): 1 | 2 | 3 =>
    c === 'verde' ? 3 : c === 'giallo' ? 2 : 1;

  const { teamName, isRelational, analyzedAt, result } = snapshot;
  const { ppt, voce_bedrock } = result;
  const themes = voce_bedrock
    .replaceAll('THEME:', 'TEMA:')
    .replaceAll('|', ', ')
    .split('TEMA:')
    .map((t) => t.trim())
    .filter((t) => t !== '');

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'html, body': { backgroundColor: 'white !important', color: 'black !important' } as any,
            '.no-print': { display: 'none !important' },
            '.print-only': { display: 'block !important' },
            '.temi-section': { pageBreakBefore: 'always', breakBefore: 'page' },
            '.MuiTypography-root': { color: 'black !important' },
            '.norm-cell': {
              WebkitPrintColorAdjust: 'exact !important',
              printColorAdjust: 'exact !important',
              filter: 'brightness(1) !important',
              animation: 'none !important',
            } as any,
            '.norm-cell .MuiTypography-root': { color: 'white !important' },
            '.MuiCard-root, .MuiPaper-root': { backgroundColor: 'white !important' },
            '.MuiDivider-root': { borderColor: '#aaa !important' },
            '.MuiLinearProgress-root': { backgroundColor: '#e0e0e0 !important' },
            '.MuiChip-root': { borderColor: '#aaa !important', color: 'black !important' },
          },
        }}
      />
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 4 } }}>
        {/* ── Header ── */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.default',
            borderBottom: 1,
            borderColor: 'divider',
            py: 2,
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
          className="no-print"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              TeamEI
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="body1" fontWeight={500}>
              {teamName}
            </Typography>
            {isRelational && (
              <Chip label="Relational System" size="small" variant="outlined" color="primary" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('report.analyzed_at', { date: analyzedAt })}
            </Typography>
            <Select
              size="small"
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              variant="standard"
              disableUnderline
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="it">IT</MenuItem>
              <MenuItem value="en">EN</MenuItem>
            </Select>
            <IconButton onClick={toggleMode} color="inherit" size="small">
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <Button size="small" variant="outlined" onClick={() => window.print()}>
              {t('report.print')}
            </Button>
            <Button size="small" onClick={() => (window.location.href = '/')}>
              {t('report.back_to_app')}
            </Button>
          </Box>
        </Box>

        {/* ── Title (visible in print) ── */}
        <Box sx={{ mb: 4, display: 'none' }} className="print-only">
          <Typography variant="h5" fontWeight={700}>
            {t('report.print_title', { teamName })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('report.analyzed_at', { date: analyzedAt })}
            {isRelational ? ' · Relational System' : ''}
          </Typography>
        </Box>

        {/* ── About TEAMEI ── */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('report.about_title')}
        </Typography>
        {isRelational ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" fontStyle="italic" color="text.secondary" sx={{ mb: 2 }}>
              {t('report.about_relational_quote')}
              <br />
              <em>{t('report.about_relational_quote_author')}</em>
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
              {t('report.about_relational_p1')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('report.about_relational_p2')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('report.about_relational_p3')}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {t('report.about_relational_tagline')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('report.about_standard_p1')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('report.about_standard_p2')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('report.about_standard_p3')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('report.about_standard_p4')}
            </Typography>
            <Typography variant="body2">{t('report.about_standard_p5')}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 4 }} />
      </Box>

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 4 }, pb: 8 }}>
        {/* ── Executive Summary ── */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('report.executive_summary')}
        </Typography>
        {/* ── PerformancePlot ── */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <PerformancePlot
            performance={normaColorToLevel(ppt.perceptionPerf)}
            potential={normaColorToLevel(ppt.probabilityPerf)}
            segmentLabels={[
              t('report.perf_emerging'),
              t('report.perf_developing'),
              t('report.perf_excelling'),
            ]}
            accentColor={primary}
            width={200}
            height={360}
          />
        </Box>

        {/* ── PerformancePlot Legend ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width={20} height={20}>
              <circle
                cx={10}
                cy={10}
                r={10}
                fill={primary}
                fillOpacity={0.15}
                stroke={primary}
                strokeWidth={2}
              />
              <circle cx={10} cy={10} r={4.5} fill={primary} />
            </svg>
            <Typography variant="caption" color="text.secondary">
              {t('report.legend_perf_dot')}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: primary,
                opacity: 0.15,
                border: `1.5px solid ${primary}`,
                borderRadius: '3px',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('report.legend_reach_area')}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width={18} height={18}>
              <defs>
                <linearGradient id="legend-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={primary} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={primary} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <rect
                x={0}
                y={0}
                width={18}
                height={18}
                fill="url(#legend-gradient)"
                rx={3}
                stroke={primary}
                strokeWidth={1.5}
                strokeOpacity={0.4}
              />
            </svg>
            <Typography variant="caption" color="text.secondary">
              {t('report.legend_gradient')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          {ppt.warnings.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Typography variant="body1" color="text.secondary" fontWeight={700}>
                {t('report.warnings_label')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {ppt.warnings
                  .map((w) => (WARNING_KEY_MAP[w] ? t(WARNING_KEY_MAP[w]) : w))
                  .join(', ')}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, maxWidth: 720, textAlign: 'justify' }}
        >
          {t('report.executive_summary_desc')}
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* ── Le Norme del Team ── */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('report.norms_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'justify' }}>
          {t('report.norms_desc')}
        </Typography>
        <NormsHeatmap normsColors={ppt.normsColors} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, textAlign: 'justify' }}>
          {t('report.norms_caption')}
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* ── Temi Emergenti ── */}
        <Box className="temi-section">
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {t('report.themes_title')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {themes.map((theme, i) => {
              const newlineIndex = theme.indexOf('\n');
              const title = newlineIndex > 0 ? theme.slice(0, newlineIndex).trim() : '';
              const body = newlineIndex > 0 ? theme.slice(newlineIndex + 1).trim() : theme;
              return (
                <Card key={i} variant="outlined">
                  <CardContent>
                    {title && (
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        {title}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {body}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
};
